// src/components/VoiceRecordingBar.jsx
// Floating "Recording · Stop" bar, mounted once at the app root. It appears
// when a voice note is still recording but the note panel that started it has
// closed (the user switched screens), so the recording can be stopped from
// anywhere. Also warns before closing / reloading the tab mid-recording.
import React, { useEffect, useState, useSyncExternalStore } from "react";
import { toast } from "react-toastify";
import { getRecorderState, stopRecording, subscribeRecorder } from "../utils/voiceRecorder";
import { RecordingElapsed } from "./OutreachPanel";

export default function VoiceRecordingBar() {
  const rs = useSyncExternalStore(subscribeRecorder, getRecorderState);
  const [stopping, setStopping] = useState(false);
  const live = !!rs.rec;

  useEffect(() => {
    if (!live) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [live]);

  if (!live || rs.ownerMounted) return null;

  const stop = async () => {
    setStopping(true);
    try {
      const taken = await stopRecording();
      if (!taken) {
        toast.info("Recording saved. Open any voice note box and click Retry to transcribe it.", { autoClose: 8000 });
      }
    } finally {
      setStopping(false);
    }
  };

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-3 rounded-full bg-slate-900/95 text-white shadow-lg pl-4 pr-1.5 py-1.5 text-xs"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}
    >
      <span className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        Recording voice note · <RecordingElapsed startedAt={rs.rec.startedAt} />
      </span>
      <button
        type="button"
        className="rounded-full bg-red-600 hover:bg-red-700 px-3 py-1 font-medium disabled:opacity-60"
        onClick={stop}
        disabled={stopping}
      >
        Stop
      </button>
    </div>
  );
}
